import React, { useRef, useEffect } from 'react';
import * as echarts from 'echarts';
import * as d3 from 'd3';
import { Card, Empty, Tabs, Typography, Table } from 'antd';

const { Title, Text } = Typography;

// 通用数据可视化组件接口
interface BioDataVisualizationProps {
  data: any;
  type: 'barChart' | 'pieChart' | 'lineChart' | 'heatmap' | 'table' | 'sequenceFeature' | 'gauge' | 'aminoProperties';
  title?: string | undefined;
  config?: any;
}

// 序列特征数据接口
interface SequenceFeature {
  start: number;
  end: number;
  type: string;
  color?: string;
  label?: string;
}

// 序列特征可视化配置
interface SequenceFeatureConfig {
  sequence: string;
  features: SequenceFeature[];
}

// 表格数据配置
interface TableConfig {
  columns: any[];
  dataSource: any[];
}

// 氨基酸属性可视化接口
interface AminoAcidPropertiesConfig {
  sequence: string;
  propertyType: 'hydrophobicity' | 'charge' | 'size' | 'polarity';
  windowSize?: number;
}

// 生物数据可视化组件
const BioDataVisualization: React.FC<Omit<BioDataVisualizationProps, 'title'> & { title?: string }> = ({
  data,
  type,
  title,
  config = {}
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const displayTitle = title || '数据可视化'; // 确保title永远有值
  
  useEffect(() => {
    if (!data || !chartRef.current) return;
    
    // 清除之前的图表
    const chartDom = chartRef.current;
    const existingChart = echarts.getInstanceByDom(chartDom);
    if (existingChart) {
      existingChart.dispose();
    }
    
    if (type === 'table' || type === 'sequenceFeature' || type === 'aminoProperties') {
      return; // 表格、序列特征和氨基酸属性在render中处理
    }
    
    const myChart = echarts.init(chartDom);
    
    switch (type) {
      case 'barChart':
        renderBarChart(myChart, data, config);
        break;
      case 'pieChart':
        renderPieChart(myChart, data, config);
        break;
      case 'lineChart':
        renderLineChart(myChart, data, config);
        break;
      case 'heatmap':
        renderHeatmap(myChart, data, config);
        break;
      case 'gauge':
        renderGauge(myChart, data, config);
        break;
      default:
        console.warn('未知的可视化类型:', type);
    }
    
    // 响应窗口大小变化
    const handleResize = () => {
      myChart.resize();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      myChart.dispose();
    };
  }, [data, type, config]);
  
  // 渲染柱状图
  const renderBarChart = (chart: echarts.ECharts, data: any, config: any) => {
    const { xAxisData, series, xAxisName = '类别', yAxisName = '数值' } = data || {};
    
    // 检查series是否存在，如果不存在则使用空数组
    const seriesData = series || [];
    
    const option = {
      title: {
        text: displayTitle,
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xAxisData || [],
        name: xAxisName,
        axisLabel: {
          interval: 0,
          rotate: config.rotateLabels ? 45 : 0
        }
      },
      yAxis: {
        type: 'value',
        name: yAxisName
      },
      series: seriesData.map((s: any) => ({
        name: s.name,
        type: 'bar',
        data: s.data,
        itemStyle: {
          color: s.color
        }
      })),
      legend: {
        orient: 'horizontal',
        bottom: 0
      }
    };
    
    chart.setOption(option);
  };
  
  // 渲染饼图
  const renderPieChart = (chart: echarts.ECharts, data: any, config: any) => {
    const { seriesData } = data;
    
    const option = {
      title: {
        text: displayTitle,
        left: 'center'
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'horizontal',
        bottom: 0
      },
      series: [
        {
          name: config.seriesName || '数据',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            formatter: '{b}: {d}%'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold'
            }
          },
          data: seriesData
        }
      ]
    };
    
    chart.setOption(option);
  };
  
  // 渲染折线图
  const renderLineChart = (chart: echarts.ECharts, data: any, config: any) => {
    const { xAxisData, series, xAxisName = 'X轴', yAxisName = 'Y轴' } = data;
    
    const option = {
      title: {
        text: displayTitle,
        left: 'center'
      },
      tooltip: {
        trigger: 'axis'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        name: xAxisName
      },
      yAxis: {
        type: 'value',
        name: yAxisName
      },
      series: series.map((s: any) => ({
        name: s.name,
        type: 'line',
        data: s.data,
        smooth: config.smooth || false,
        itemStyle: {
          color: s.color
        },
        showSymbol: config.showSymbol !== undefined ? config.showSymbol : true,
        lineStyle: {
          width: config.showLine === false ? 0 : 2
        },
        symbolSize: s.symbolSize || 4
      })),
      legend: {
        orient: 'horizontal',
        bottom: 0
      }
    };
    
    chart.setOption(option);
  };
  
  // 渲染热图
  const renderHeatmap = (chart: echarts.ECharts, data: any, config: any) => {
    const { xAxisData, yAxisData, seriesData } = data;
    
    const option = {
      title: {
        text: displayTitle,
        left: 'center'
      },
      tooltip: {
        position: 'top'
      },
      grid: {
        height: '70%',
        top: '10%'
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        splitArea: {
          show: true
        }
      },
      yAxis: {
        type: 'category',
        data: yAxisData,
        splitArea: {
          show: true
        }
      },
      visualMap: {
        min: config.min || 0,
        max: config.max || 1,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: '0%'
      },
      series: [{
        name: config.seriesName || '热图',
        type: 'heatmap',
        data: seriesData,
        label: {
          show: config.showLabels || false
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }]
    };
    
    chart.setOption(option);
  };
  
  // 渲染仪表盘
  const renderGauge = (chart: echarts.ECharts, data: any, config: any) => {
    const { seriesData } = data;
    const { min = 0, max = 100, splitNumber = 10, threshold = null, thresholds = null } = config;
    
    // 定义颜色区域
    let axisLine = {};
    if (thresholds && Array.isArray(thresholds) && thresholds.length > 1) {
      // 使用自定义阈值分段
      const colorStops = [];
      for (let i = 1; i < thresholds.length; i++) {
        const stop = thresholds[i] / max;
        const prevStop = thresholds[i-1] / max;
        const color = i === 1 ? '#52c41a' : (i === 2 ? '#faad14' : '#f5222d'); // 绿-黄-红
        colorStops.push([stop, color]);
      }
      axisLine = {
        lineStyle: {
          width: 30,
          color: colorStops
        }
      };
    } else if (threshold !== null) {
      // 如果有单一阈值，根据阈值分区域显示颜色
      axisLine = {
        lineStyle: {
          width: 30,
          color: [
            [threshold / max, '#52c41a'], // 绿色表示稳定区域
            [1, '#f5222d'] // 红色表示不稳定区域
          ]
        }
      };
    } else {
      // 默认颜色
      axisLine = {
        lineStyle: {
          width: 30,
          color: [
            [0.3, '#52c41a'],
            [0.7, '#faad14'],
            [1, '#f5222d']
          ]
        }
      };
    }
    
    // 数据的显示和配置
    const gaugeData = seriesData.map((item: any) => ({
      value: item.value,
      name: item.name,
      title: {
        color: item.color || 'inherit'
      },
      detail: {
        color: item.color || 'inherit',
        formatter: '{value}'
      }
    }));

    // 设置仪表盘标题
    let gaugeTitle = displayTitle;
    if (seriesData.length > 0) {
      gaugeTitle = title || seriesData[0].name;
    }
    
    const option = {
      title: {
        text: gaugeTitle,
        left: 'center',
        top: '10px',
        padding: [0, 0, 20, 0],
        textStyle: {
          fontSize: 16
        }
      },
      tooltip: {
        formatter: '{a} <br/>{b} : {c}'
      },
      series: [
        {
          name: '指标',
          type: 'gauge',
          min,
          max,
          splitNumber,
          radius: '85%',
          axisLine,
          pointer: {
            length: '60%',
            width: 6,
            itemStyle: {
              color: 'auto'
            }
          },
          axisTick: {
            distance: -18,
            length: 10,
            lineStyle: {
              color: '#fff',
              width: 2
            }
          },
          splitLine: {
            distance: -20,
            length: 20,
            lineStyle: {
              color: '#fff',
              width: 3
            }
          },
          axisLabel: {
            color: 'inherit',
            distance: 35,
            fontSize: 12
          },
          detail: {
            valueAnimation: true,
            formatter: '{value}',
            color: 'inherit',
            fontSize: 24,
            offsetCenter: [0, '80%'],
            padding: [10, 0, 0, 0],
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            borderRadius: 5,
            textBorderColor: 'rgba(0, 0, 0, 0.1)',
            textBorderWidth: 1
          },
          title: {
            fontSize: 14,
            offsetCenter: [0, '110%'],
            fontWeight: 'bold',
            color: '#333'
          },
          data: gaugeData
        }
      ]
    };
    
    chart.setOption(option);
  };
  
  // 渲染序列特征可视化
  const renderSequenceFeature = () => {
    const { sequence, features } = config as SequenceFeatureConfig;
    
    if (!sequence || !features) {
      return <Empty description="缺少序列或特征数据" />;
    }
    
    return (
      <div style={{ padding: '10px' }}>
        <Title level={5}>{displayTitle}</Title>
        <div style={{ 
          position: 'relative', 
          height: '80px', 
          background: '#f5f5f5', 
          borderRadius: '4px',
          marginTop: '20px',
          marginBottom: '40px'
        }}>
          {/* 序列主线 */}
          <div style={{ 
            position: 'absolute', 
            top: '40px', 
            left: '0', 
            width: '100%', 
            height: '2px', 
            background: '#ccc' 
          }} />
          
          {/* 刻度标记 */}
          {Array.from({ length: 11 }).map((_, i) => (
            <div key={i} style={{ 
              position: 'absolute', 
              top: '35px', 
              left: `${i * 10}%`, 
              width: '1px', 
              height: '10px', 
              background: '#999' 
            }}>
              <div style={{ 
                position: 'absolute', 
                top: '15px', 
                left: '-15px', 
                width: '30px', 
                textAlign: 'center',
                fontSize: '12px'
              }}>
                {Math.floor(sequence.length * i / 10)}
              </div>
            </div>
          ))}
          
          {/* 特征区域 */}
          {features.map((feature, index) => {
            const startPercent = (feature.start / sequence.length) * 100;
            const widthPercent = ((feature.end - feature.start + 1) / sequence.length) * 100;
            
            return (
              <div key={index}>
                <div style={{ 
                  position: 'absolute', 
                  top: '30px', 
                  left: `${startPercent}%`, 
                  width: `${widthPercent}%`, 
                  height: '20px', 
                  background: feature.color || '#1890ff', 
                  borderRadius: '3px',
                  opacity: 0.7,
                  zIndex: 2
                }} />
                
                {feature.label && (
                  <div style={{ 
                    position: 'absolute', 
                    top: '55px', 
                    left: `${startPercent + widthPercent/2 - 5}%`, 
                    transform: 'translateX(-50%)',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    {feature.label}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  // 渲染表格
  const renderTable = () => {
    const { columns, dataSource } = config as TableConfig;
    
    if (!columns || !dataSource) {
      return <Empty description="缺少表格数据" />;
    }
    
    return (
      <div>
        <Title level={5}>{displayTitle}</Title>
        <Table 
          columns={columns} 
          dataSource={dataSource} 
          pagination={false} 
          size="small"
          bordered
        />
      </div>
    );
  };

  // 渲染氨基酸属性可视化 - 从 BioVisualization.tsx 整合
  const renderAminoAcidProperties = () => {
    const { sequence, propertyType, windowSize = 7 } = config as AminoAcidPropertiesConfig;
    const d3Container = useRef<HTMLDivElement>(null);
    
    // 氨基酸属性数据
    const properties = {
      hydrophobicity: {
        A: 1.8, R: -4.5, N: -3.5, D: -3.5, C: 2.5, Q: -3.5, E: -3.5, G: -0.4,
        H: -3.2, I: 4.5, L: 3.8, K: -3.9, M: 1.9, F: 2.8, P: -1.6, S: -0.8,
        T: -0.7, W: -0.9, Y: -1.3, V: 4.2
      },
      charge: {
        A: 0, R: 1, N: 0, D: -1, C: 0, Q: 0, E: -1, G: 0,
        H: 0.1, I: 0, L: 0, K: 1, M: 0, F: 0, P: 0, S: 0,
        T: 0, W: 0, Y: 0, V: 0
      },
      size: {
        A: 88.6, R: 173.4, N: 114.1, D: 111.1, C: 108.5, Q: 143.8, E: 138.4, G: 60.1,
        H: 153.2, I: 166.7, L: 166.7, K: 168.6, M: 162.9, F: 189.9, P: 112.7, S: 89.0,
        T: 116.1, W: 227.8, Y: 193.6, V: 140.0
      },
      polarity: {
        A: 0, R: 52, N: 3.38, D: 40, C: 1.48, Q: 3.53, E: 49.91, G: 0,
        H: 51.6, I: 0.13, L: 0.13, K: 49.5, M: 1.43, F: 0.35, P: 1.58, S: 1.67,
        T: 1.66, W: 2.1, Y: 1.61, V: 0.13
      }
    };
    
    // 辅助函数：获取属性名称
    const getPropertyTypeName = (type: string) => {
      switch(type) {
        case 'hydrophobicity': return '疏水性';
        case 'charge': return '电荷';
        case 'size': return '大小';
        case 'polarity': return '极性';
        default: return type;
      }
    };
    
    // 辅助函数：获取属性颜色
    const getPropertyColor = (type: string) => {
      switch(type) {
        case 'hydrophobicity': return '#4682B4'; // 钢蓝色
        case 'charge': return '#CD5C5C'; // 印度红
        case 'size': return '#2E8B57'; // 海洋绿
        case 'polarity': return '#8A2BE2'; // 紫罗兰色
        default: return '#1890ff';
      }
    };
    
    useEffect(() => {
      if (!sequence || !d3Container.current) return;
      
      // 计算滑动窗口平均值
      const calculateWindowAverage = (seq: string, propType: keyof typeof properties, windowSize: number) => {
        const values = [];
        const props = properties[propType];
        
        for (let i = 0; i < seq.length; i++) {
          let sum = 0;
          let count = 0;
          
          for (let j = Math.max(0, i - Math.floor(windowSize / 2)); 
               j <= Math.min(seq.length - 1, i + Math.floor(windowSize / 2)); j++) {
            const aa = seq[j].toUpperCase();
            if (props[aa as keyof typeof props] !== undefined) {
              sum += props[aa as keyof typeof props];
              count++;
            }
          }
          
          values.push(count > 0 ? sum / count : 0);
        }
        
        return values;
      };
      
      // 获取属性值
      const propertyValues = calculateWindowAverage(sequence, propertyType, windowSize);
      
      // 清除之前的图表
      d3.select(d3Container.current).selectAll("*").remove();
      
      // 设置图表尺寸和边距
      const margin = { top: 50, right: 30, bottom: 50, left: 50 };
      const width = d3Container.current.clientWidth - margin.left - margin.right;
      const height = 300 - margin.top - margin.bottom;
      
      // 创建SVG容器
      const svg = d3.select(d3Container.current)
        .append("svg")
          .attr("width", width + margin.left + margin.right)
          .attr("height", height + margin.top + margin.bottom)
        .append("g")
          .attr("transform", `translate(${margin.left},${margin.top})`);
      
      // 添加标题
      const chartTitle = displayTitle || `氨基酸${getPropertyTypeName(propertyType)}分析 (窗口大小: ${windowSize})`;
      svg.append("text")
        .attr("x", width / 2)
        .attr("y", -margin.top / 2)
        .attr("text-anchor", "middle")
        .style("font-size", "16px")
        .style("font-weight", "bold")
        .text(chartTitle);
      
      // 创建X轴比例尺
      const x = d3.scaleLinear()
        .domain([1, sequence.length])
        .range([0, width]);
      
      // 创建Y轴比例尺
      const y = d3.scaleLinear()
        .domain([d3.min(propertyValues) || 0, d3.max(propertyValues) || 0])
        .nice()
        .range([height, 0]);
      
      // 添加X轴
      svg.append("g")
        .attr("transform", `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(10));
      
      // 添加X轴标签
      svg.append("text")
        .attr("transform", `translate(${width/2}, ${height + margin.bottom - 10})`)
        .style("text-anchor", "middle")
        .text("序列位置");
      
      // 添加Y轴
      svg.append("g")
        .call(d3.axisLeft(y));
      
      // 添加Y轴标签
      svg.append("text")
        .attr("transform", "rotate(-90)")
        .attr("y", -margin.left + 15)
        .attr("x", -height / 2)
        .style("text-anchor", "middle")
        .text(getPropertyTypeName(propertyType));
      
      // 创建线条生成器
      const line = d3.line<number>()
        .x((d, i) => x(i + 1))
        .y(d => y(d));
      
      // 绘制线条
      svg.append("path")
        .datum(propertyValues)
        .attr("fill", "none")
        .attr("stroke", getPropertyColor(propertyType))
        .attr("stroke-width", 2)
        .attr("d", line);
      
      // 添加零线（如果适用）
      if (propertyType === 'charge' || propertyType === 'hydrophobicity') {
        svg.append("line")
          .attr("x1", 0)
          .attr("y1", y(0))
          .attr("x2", width)
          .attr("y2", y(0))
          .attr("stroke", "#888")
          .attr("stroke-width", 1)
          .attr("stroke-dasharray", "4");
      }
      
      // 添加提示工具
      const tooltip = d3.select(d3Container.current)
        .append("div")
        .style("opacity", 0)
        .attr("class", "tooltip")
        .style("background-color", "white")
        .style("border", "solid")
        .style("border-width", "1px")
        .style("border-radius", "5px")
        .style("padding", "5px")
        .style("position", "absolute");
      
      // 添加交互区域
      svg.append("rect")
        .attr("width", width)
        .attr("height", height)
        .attr("fill", "none")
        .attr("pointer-events", "all")
        .on("mousemove", function(event) {
          const mouse = d3.pointer(event);
          const pos = Math.round(x.invert(mouse[0]));
          
          if (pos < 1 || pos > sequence.length) return;
          
          // 更新提示内容
          tooltip
            .style("opacity", 1)
            .style("left", (event.pageX + 10) + "px")
            .style("top", (event.pageY - 30) + "px")
            .html(`位置: ${pos}<br>氨基酸: ${sequence[pos-1]}<br>${getPropertyTypeName(propertyType)}: ${propertyValues[pos-1].toFixed(2)}`);
        })
        .on("mouseout", function() {
          tooltip.style("opacity", 0);
        });
    }, [sequence, propertyType, windowSize, displayTitle]);
    
    if (!sequence) {
      return <Empty description="请提供序列数据" />;
    }
    
    return <div ref={d3Container} style={{ height: 300, width: '100%' }}></div>;
  };
  
  if (!data) {
    return <Empty description="无数据可视化" />;
  }
  
  return (
    <Card title={displayTitle} bordered={true}>
      {type === 'table' ? (
        renderTable()
      ) : type === 'sequenceFeature' ? (
        renderSequenceFeature()
      ) : type === 'aminoProperties' ? (
        renderAminoAcidProperties()
      ) : (
        <div ref={chartRef} style={{ height: config.height || '400px', width: '100%' }} />
      )}
    </Card>
  );
};

export default BioDataVisualization; 